/**
 * SetupReminderCenter — Megaphone icon with account setup reminders + Getting Started checklist.
 *
 * Shows a notification dot when the user has any pending item:
 *   1. Set a password (has_password = false)
 *   2. Added business info (company_name / business_type missing)
 *   3. Getting Started checklist not yet dismissed/completed
 *
 * Clicking opens a popover with reminder cards and the checklist.
 * Once all items are completed/dismissed the icon disappears.
 */

import { useState, useEffect, useCallback } from "react";
import { Megaphone, Shield, Building2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useOnboarding } from "@/hooks/useOnboarding";
import { supabase } from "@/integrations/supabase/client";
import { SetPasswordModal } from "@/components/onboarding/SetPasswordModal";
import { OnboardingChecklist } from "@/components/onboarding/OnboardingChecklist";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface ProfileSetup {
  hasPassword: boolean;
  hasBusinessInfo: boolean;
  userEmail: string | null;
}

export function SetupReminderCenter() {
  const { t } = useLanguage();
  const { user, loading: authLoading } = useAuth(false);
  const isMobile = useIsMobile();
  const { state: onboardingState, isChecklistComplete } = useOnboarding();

  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState<ProfileSetup | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showBusinessModal, setShowBusinessModal] = useState(false);

  // Business info form state
  const [companyName, setCompanyName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading || !user) return;

    const fetchProfile = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("company_name, business_type")
        .eq("id", user.id)
        .maybeSingle();

      if (error || !data) return;

      // Derive hasPassword from auth user metadata (magic-link users have password_set=false)
      const hasPassword = (user.user_metadata as Record<string, unknown> | undefined)?.password_set !== false;

      setProfile({
        hasPassword,
        hasBusinessInfo: !!(data.company_name && data.business_type),
        userEmail: user.email ?? null,
      });
    };

    fetchProfile();
  }, [user, authLoading]);

  const handlePasswordSet = useCallback(() => {
    setProfile((prev) => (prev ? { ...prev, hasPassword: true } : null));
    setShowPasswordModal(false);
  }, []);

  const handleBusinessSave = useCallback(async () => {
    if (!user?.id || !companyName.trim()) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          company_name: companyName.trim(),
          business_type: businessType || null,
        })
        .eq("id", user.id);

      if (error) throw error;

      toast.success(t("businessInfo.saved"));
      setProfile((prev) => (prev ? { ...prev, hasBusinessInfo: true } : null));
      setShowBusinessModal(false);
    } catch {
      toast.error(t("settings.saveFailed"));
    } finally {
      setSaving(false);
    }
  }, [user, companyName, businessType, t]);

  const checklistPending =
    !isChecklistComplete && !onboardingState.checklistDismissed;

  const profilePending =
    profile !== null && (!profile.hasPassword || !profile.hasBusinessInfo);

  const hasPending = profilePending || checklistPending;

  // Don't render until profile is loaded; hide when nothing is pending
  if (profile === null || !hasPending) {
    return null;
  }

  const triggerButton = (
    <Button
      variant={isMobile ? "ghost" : "ghost-utility"}
      size="icon"
      className={cn(
        "relative",
        isMobile
          ? "h-11 w-11 text-white/80 hover:text-white hover:bg-white/10"
          : "h-8 w-8"
      )}
      aria-label={t("setupReminder.title")}
    >
      <Megaphone className={isMobile ? "h-4 w-4" : "h-3.5 w-3.5"} />
      {/* Persistent notification dot — visible until all items are done */}
      <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-destructive" />
    </Button>
  );

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>

        <PopoverContent
          align="end"
          sideOffset={8}
          className="w-[calc(100vw-2rem)] max-w-sm p-0 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center border-b px-3 py-2">
            <span className="text-sm font-medium">{t("setupReminder.title")}</span>
          </div>

          {/* Content */}
          <div className="p-3 space-y-3">
            {/* Getting Started checklist */}
            {checklistPending && (
              <OnboardingChecklist />
            )}

            {/* Secure account card */}
            {!profile.hasPassword && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-foreground">
                      {t("secureAccount.title")}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("secureAccount.description")}
                    </p>
                    {profile.userEmail && (
                      <p className="text-xs text-muted-foreground/70 mt-1.5 flex items-center gap-1.5">
                        <Mail className="h-3 w-3 shrink-0" />
                        <span
                          className="truncate max-w-[200px]"
                          title={profile.userEmail}
                        >
                          {t("secureAccount.emailLabel")}: {profile.userEmail}
                        </span>
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        size="sm"
                        onClick={() => {
                          setOpen(false);
                          setShowPasswordModal(true);
                        }}
                        className="h-8 text-xs"
                      >
                        {t("secureAccount.setPassword")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOpen(false)}
                        className="h-8 text-xs text-muted-foreground"
                      >
                        {t("secureAccount.later")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Business info card */}
            {!profile.hasBusinessInfo && (
              <div className="bg-secondary/50 border border-border/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Building2 className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-foreground">
                      {t("businessInfo.title")}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("businessInfo.description")}
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setOpen(false);
                          setShowBusinessModal(true);
                        }}
                        className="h-8 text-xs"
                      >
                        {t("businessInfo.addInfo")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOpen(false)}
                        className="h-8 text-xs text-muted-foreground"
                      >
                        {t("businessInfo.later")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Set password modal */}
      <SetPasswordModal
        open={showPasswordModal}
        onOpenChange={setShowPasswordModal}
        onSuccess={handlePasswordSet}
      />

      {/* Business info modal */}
      <Dialog open={showBusinessModal} onOpenChange={setShowBusinessModal}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-[95vw] max-w-md min-w-0">
          <DialogHeader>
            <DialogTitle>{t("businessInfo.modalTitle")}</DialogTitle>
            <DialogDescription>{t("businessInfo.modalDescription")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="setup-company-name">{t("businessInfo.companyName")}</Label>
              <Input
                id="setup-company-name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder={t("businessInfo.companyNamePlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="setup-business-type">{t("businessInfo.businessType")}</Label>
              <Select value={businessType} onValueChange={setBusinessType}>
                <SelectTrigger>
                  <SelectValue placeholder={t("businessInfo.selectType")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bakery">{t("businessInfo.types.bakery")}</SelectItem>
                  <SelectItem value="restaurant">{t("businessInfo.types.restaurant")}</SelectItem>
                  <SelectItem value="retail">{t("businessInfo.types.retail")}</SelectItem>
                  <SelectItem value="delivery">{t("businessInfo.types.delivery")}</SelectItem>
                  <SelectItem value="other">{t("businessInfo.types.other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowBusinessModal(false)}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleBusinessSave}
                disabled={!companyName.trim() || saving}
              >
                {saving ? t("common.saving") : t("common.save")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
